import { useRef, useCallback, useState } from 'react';
import { getAllUnexpectedStockOutflowStatus } from 'App/hexmodules/unexpectedStockOutflowStatus/application/getAll/getAllUnexpectedStockOutflowStatus'
import { getAllInventoryReasons } from 'App/hexmodules/inventoryReasons/application/getAll/getAllInventoryReasons'
import { createUnexpectedStockOutflow } from 'App/hexmodules/unexpectedStockOutflow/application/create/createUnexpectedStockOutflow'
import { uploadUnexpectedStockOutflowFile } from 'App/hexmodules/unexpectedStockOutflow/application/upload/uploadUnexpectedStockOutflowFile'
import { getUnexpectedStockOutflow } from 'App/hexmodules/unexpectedStockOutflow/application/get/getUnexpectedStockOutflow'
import { deleteUnexpectedStockOutflow } from 'App/hexmodules/unexpectedStockOutflow/application/delete/deleteUnexpectedStockOutflow'
import { useDependencies } from 'App/DependencyContext';
import useForm from 'App/hooks/useForm'
import { unexpectedStockOutflowDetail } from './data.js'

const STATE = {
  INITIAL: 'initial',
  FETCHING_GENERAL_DATA: 'fetchingGeneralData',
  FETCHED_GENERAL_DATA: 'fetchedGeneralData',
  ERROR_FETCHING_GENERAL_DATA: 'errorFetchingGeneralData',
  SAVING_GENERAL_DATA: 'savingGeneralData',
  SAVED_GENERAL_DATA: 'savedGeneralData',
  ERROR_SAVING_GENERAL_DATA: 'errorSavingGeneralData',
  UPLOADING_FILE: 'uploadingFile',
  FILE_UPLOADED: 'fileUploaded',
  ERROR_UPLOADING_FILE: 'errorUploadingFile'
};

function useUnexpectedStockOutflow({ idUnexpectedStockOutflow }) {
  const state = useRef(STATE.INITIAL);
  const [unexpectedStockOutflowStatus, setUnexpectedStockOutflowStatus] = useState([])
  const [inventoryReasons, setInventoryReasons] = useState([])
  const { unexpectedStockOutflowStatusRepository, inventoryReasonsRepository, unexpectedStockOutflowRepository } = useDependencies()
  const [
    formFields, setFormFields, onFormFieldChange,
    isValidForm, getFormValues, showErrors,
  ] = useForm({
    fields: unexpectedStockOutflowDetail,
    initialValid: true,
  })
  const [status, setStatus] = useState(unexpectedStockOutflowDetail.estatus.input.value)
  const [files, setFiles] = useState([])
  const [product, setProduct] = useState(undefined)


  const fetchStatus = async () => {
    const data = await getAllUnexpectedStockOutflowStatus(unexpectedStockOutflowStatusRepository);
    setUnexpectedStockOutflowStatus(data)
  }

  const fetchReasons = async () => {
    const data = await getAllInventoryReasons(inventoryReasonsRepository);
    setInventoryReasons(data)
  }

  const fetchUnexpectedStockOutflow = async () => {
    try {
      state.current = STATE.FETCHING_GENERAL_DATA;
      const data = await getUnexpectedStockOutflow(unexpectedStockOutflowRepository, idUnexpectedStockOutflow);
      setStatus(data.estatus)
      setProduct(data.producto)
      const {adjuntos, ...outflow} = data
      setFormFields(outflow)
      setFiles(adjuntos)
      state.current = STATE.FETCHED_GENERAL_DATA;
    } catch (error) {
      state.current = STATE.ERROR_FETCHING_GENERAL_DATA;
      throw error;
    }
  }

  const create = async (data) => {
    try {
      state.current = STATE.SAVING_GENERAL_DATA;
      const result = await createUnexpectedStockOutflow(unexpectedStockOutflowRepository, {...data, adjuntos: files});
      const {adjuntos, ...outflow} = result
      setFormFields(outflow)
      setFiles(adjuntos)
      state.current = STATE.SAVED_GENERAL_DATA;
      return result;
    } catch (error) {
      state.current = STATE.ERROR_SAVING_GENERAL_DATA;
      throw error;
    }
  }

  const update = async (id, data) => {
    try {
      state.current = STATE.SAVING_GENERAL_DATA;
      const result = await unexpectedStockOutflowRepository.update(id, {...data, adjuntos: files});
      const {adjuntos, ...outflow} = result
      setFormFields(outflow)
      setFiles(adjuntos)
      state.current = STATE.SAVED_GENERAL_DATA;
      return result;
    } catch (error) {
      state.current = STATE.ERROR_SAVING_GENERAL_DATA;
      throw error;
    }
  }

  const upload = async (file) => {
    try {
      state.current = STATE.UPLOADING_FILE;
      const uploadedFile = uploadUnexpectedStockOutflowFile(unexpectedStockOutflowRepository, file);
      state.current = STATE.FILE_UPLOADED;
      return uploadedFile;
    } catch (error) {
      state.current = STATE.ERROR_UPLOADING_FILE;
      throw error;
    }
  }

  const initialFetch = async () => {
    if (state.current === STATE.INITIAL) {
      state.current = STATE.FETCHING_GENERAL_DATA;
      await Promise.all([
        fetchStatus(),
        fetchReasons()
      ]);
      if (idUnexpectedStockOutflow) {
        await fetchUnexpectedStockOutflow();
      }
      state.current = STATE.FETCHED_GENERAL_DATA;
    }
  };

  initialFetch();

  const getCurrentState = useCallback(() => {
    return state.current;
  }, []);

  const deleteUnexpectedStock = async (id) => {
    try {
      state.current = STATE.SAVING_GENERAL_DATA;
      await deleteUnexpectedStockOutflow(unexpectedStockOutflowRepository, id);
      state.current = STATE.SAVED_GENERAL_DATA;
    } catch (error) {
      state.current = STATE.ERROR_SAVING_GENERAL_DATA;
      throw error;
    }
  };

  return {
    getCurrentState,
    unexpectedStockOutflowStatus,
    inventoryReasons,
    refreshStatus: fetchStatus,
    refreshReasons: fetchReasons,
    create,
    update,
    upload,
    form: {
      formFields,
      setFormFields,
      onFormFieldChange,
      isValidForm,
      getFormValues,
      showErrors,
      setFiles,
      files,
    },
    currentStatus: status,
    initialProductValue: product,
    state: state.current,
    deleteOutflow: deleteUnexpectedStock,
  };
}

export default useUnexpectedStockOutflow;